import React, { useEffect, useState } from 'react';
import { neonQuery } from '../services/api';

// Email-confirmation-style order summary. Opened from the VIP Cash
// modal when a row's order_id is clicked. Renders ABOVE the cash modal
// (z-index 1010) so closing this returns to the cash ledger context.

const fmt$ = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : Number(String(v).trim());
  return isFinite(n) ? n : 0;
};
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function joinName(first, last) {
  return [first, last].filter(Boolean).join(' ').trim();
}

// Item names come out of Neon with embedded HTML (e.g.
// "1/2 Bushel ...<br><span>Steaming Options:</span><b>Steamed</b>").
// React escapes that, so the raw tags show as literal text. Split on
// <br> into a main name + option lines, then strip any remaining tags.
function parseItemName(raw) {
  const segments = String(raw || '')
    .split(/<br\s*\/?>/i)
    .map((s) => s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  return { main: segments[0] || '', options: segments.slice(1) };
}

function pickBilling(o) {
  // Prefer the joined addresses-table values; fall back to legacy
  // denormalized fields on the order itself.
  return {
    name: joinName(o.bill_first, o.bill_last) || joinName(o.ofirstname, o.olastname),
    addr1: o.bill_addr1 || o.oaddress,
    addr2: o.bill_addr2 || o.oaddress2,
    city: o.bill_city || o.ocity,
    state: o.bill_state || o.ostate,
    zip: o.bill_zip || o.opostcode,
    phone: o.bill_phone || o.ophone,
  };
}

function pickShipping(o) {
  return {
    name: joinName(o.ship_first, o.ship_last) || o.oshipfullname || joinName(o.oshipname, o.oshiplastname),
    addr1: o.ship_addr1 || o.oshipaddress,
    addr2: o.ship_addr2 || o.oshipaddress2,
    city: o.ship_city || o.oshiptown,
    state: o.ship_state || o.oshipstate,
    zip: o.ship_zip || o.oshipzip,
    phone: o.ship_phone || o.oshipphone,
  };
}

function AddressBlock({ label, addr }) {
  if (!addr || (!addr.name && !addr.addr1)) return null;
  return (
    <div style={{ flex: '1 1 0', minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: 'var(--navy)', lineHeight: 1.4 }}>
        {addr.name && <div style={{ fontWeight: 600 }}>{addr.name}</div>}
        {addr.addr1 && <div>{addr.addr1}</div>}
        {addr.addr2 && <div>{addr.addr2}</div>}
        {(addr.city || addr.state || addr.zip) && (
          <div>
            {[addr.city, addr.state].filter(Boolean).join(', ')}
            {addr.zip ? ` ${addr.zip}` : ''}
          </div>
        )}
        {addr.phone && <div style={{ color: 'var(--gray-500)' }}>{addr.phone}</div>}
      </div>
    </div>
  );
}

export default function OrderSummaryModal({ open, onClose, bcOrderId }) {
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !bcOrderId) return;
    let cancelled = false;
    setLoading(true); setError(null); setOrder(null); setItems([]);
    neonQuery('order_by_id', { orderId: String(bcOrderId) })
      .then((rows) => {
        if (cancelled) return null;
        const o = (rows || [])[0];
        if (!o) { setError('Order not found in Neon'); return null; }
        setOrder(o);
        return neonQuery('order_items', { orderId: String(o.orderid) });
      })
      .then((rows) => { if (!cancelled && rows) setItems(Array.isArray(rows) ? rows : []); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, bcOrderId]);

  if (!open) return null;

  const itemsSubtotal = items.reduce((s, it) => s + num(it.unit_price) * num(it.quantity), 0);
  const shipCost = order ? num(order.oshipcost) : 0;
  const tax = order ? num(order.otax) : 0;
  const couponDiscount = order ? num(order.coupondiscount || order.coupon_discount) : 0;
  const giftUsed = order ? num(order.giftamountused || order.gift_amount_used) : 0;
  const manualDiscount = order ? num(order.manual_discount) : 0;
  const adjustment = order ? num(order.adjustment ?? order.oadjustment) : 0;
  const adjustmentDescr = order ? (order.adjustment_description || order.oadjustmentdescr || '') : '';
  const handling = order ? num(order.ohandling || order.handling) : 0;
  const orderTotal = order ? num(order.orderamount) : 0;

  const shipMethod = order ? (order.oshipmethod || order.oshipmethodtype || '') : '';

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10, 20, 40, 0.55)', zIndex: 1010,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(720px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', margin: 0 }}
      >
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>
            Order #{bcOrderId}
            {order && order.odate && (
              <span style={{ color: 'var(--gray-500)', fontWeight: 400, marginLeft: 8 }}>
                · {fmtDate(order.odate)}
              </span>
            )}
          </span>
          <a onClick={onClose} style={{ cursor: 'pointer', color: 'var(--gray-500)', fontSize: 18, fontWeight: 700, lineHeight: 1 }}>×</a>
        </div>

        <div style={{ overflow: 'auto', flex: 1, padding: '14px 16px' }}>
          {loading && <div style={{ fontSize: 12, color: 'var(--gray-500)', textAlign: 'center', padding: 20 }}>Loading order…</div>}
          {error && <div style={{ fontSize: 12, color: 'var(--red, #c0392b)', padding: 12 }}>Failed to load: {error}</div>}

          {order && (
            <>
              {/* Items */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  Items {items.length > 0 && <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}>({items.length})</span>}
                </div>
                {items.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No line items found.</div>
                ) : (
                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)' }}>Item</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)', width: 50 }}>Qty</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)', width: 80 }}>Price</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)', width: 90 }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, i) => {
                        const lineTotal = num(it.unit_price) * num(it.quantity);
                        return (
                          <tr key={it.id ?? i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                            <td style={{ padding: '6px 8px', color: 'var(--navy)' }}>
                              {(() => {
                                const { main, options } = parseItemName(it.name);
                                return (
                                  <>
                                    {main}
                                    {it.sku && <span style={{ color: 'var(--gray-500)', marginLeft: 6, fontSize: 10 }}>{it.sku}</span>}
                                    {options.map((o, oi) => (
                                      <div key={oi} style={{ color: 'var(--gray-500)', fontSize: 11 }}>{o}</div>
                                    ))}
                                  </>
                                );
                              })()}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{num(it.quantity)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt$(it.unit_price)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{fmt$(lineTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Totals */}
              <div style={{ marginBottom: 16, borderTop: '1px solid var(--gray-200)', paddingTop: 10 }}>
                <table style={{ width: '100%', maxWidth: 320, marginLeft: 'auto', fontSize: 12 }}>
                  <tbody>
                    {itemsSubtotal > 0 && (
                      <tr><td style={{ padding: '2px 8px', color: 'var(--gray-700)' }}>Subtotal</td><td style={{ padding: '2px 8px', textAlign: 'right' }}>{fmt$(itemsSubtotal)}</td></tr>
                    )}
                    {Math.abs(couponDiscount) > 0.01 && (
                      <tr><td style={{ padding: '2px 8px', color: 'var(--gray-700)' }}>Coupon</td><td style={{ padding: '2px 8px', textAlign: 'right', color: 'var(--orange)' }}>−{fmt$(Math.abs(couponDiscount))}</td></tr>
                    )}
                    {Math.abs(manualDiscount) > 0.01 && (
                      <tr><td style={{ padding: '2px 8px', color: 'var(--gray-700)' }}>Manual discount</td><td style={{ padding: '2px 8px', textAlign: 'right', color: 'var(--orange)' }}>−{fmt$(Math.abs(manualDiscount))}</td></tr>
                    )}
                    {Math.abs(giftUsed) > 0.01 && (
                      <tr><td style={{ padding: '2px 8px', color: 'var(--gray-700)' }}>Gift card</td><td style={{ padding: '2px 8px', textAlign: 'right', color: 'var(--orange)' }}>−{fmt$(Math.abs(giftUsed))}</td></tr>
                    )}
                    {Math.abs(adjustment) > 0.01 && (
                      <tr><td style={{ padding: '2px 8px', color: 'var(--gray-700)' }}>{adjustmentDescr || 'Adjustment'}</td><td style={{ padding: '2px 8px', textAlign: 'right', color: adjustment < 0 ? 'var(--orange)' : 'var(--gray-700)' }}>{adjustment < 0 ? '−' : ''}{fmt$(Math.abs(adjustment))}</td></tr>
                    )}
                    {shipCost > 0 && (
                      <tr><td style={{ padding: '2px 8px', color: 'var(--gray-700)' }}>Shipping{shipMethod ? ` · ${shipMethod}` : ''}</td><td style={{ padding: '2px 8px', textAlign: 'right' }}>{fmt$(shipCost)}</td></tr>
                    )}
                    {handling > 0 && (
                      <tr><td style={{ padding: '2px 8px', color: 'var(--gray-700)' }}>Handling</td><td style={{ padding: '2px 8px', textAlign: 'right' }}>{fmt$(handling)}</td></tr>
                    )}
                    {tax > 0 && (
                      <tr><td style={{ padding: '2px 8px', color: 'var(--gray-700)' }}>Tax</td><td style={{ padding: '2px 8px', textAlign: 'right' }}>{fmt$(tax)}</td></tr>
                    )}
                    <tr style={{ borderTop: '1px solid var(--gray-200)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 700, color: 'var(--navy)' }}>Total</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>{fmt$(orderTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Addresses */}
              <div style={{ display: 'flex', gap: 24, paddingTop: 10, borderTop: '1px solid var(--gray-200)' }}>
                <AddressBlock label="Bill to" addr={pickBilling(order)} />
                <AddressBlock label="Ship to" addr={pickShipping(order)} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
